import React, { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Plus, ArrowUp, ArrowDown, ArrowUpDown, MoreHorizontal, FolderKanban } from 'lucide-react'
import Popup from 'components/Popup'
import Spinner from 'components/Spinner'
import Message from 'components/Message'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import {
	Table,
	TableHeader,
	TableBody,
	TableHead,
	TableRow,
	TableCell,
} from 'components/ui/table'
import { patchLocation } from 'slices/locationSlice'
import { useParams } from 'react-router-dom'
import { createProject, deleteProject, initializeProjects, patchProject } from 'slices/projectSlice'
import ProjectAddPopup from 'components/popups/ProjectAddPopup'
import ProjectActionSheet from 'screens/projects/components/Projects/ProjectActionSheet'
import Button from 'components/Button'
import LocationProjectsEmptyState from 'components/LocationProjectsEmptyState'
import { CardGrid, ProjectCard } from 'components/Card'
import useMediaQuery from 'hooks/useMediaQuery'
import useCapabilities from 'hooks/useCapabilities'
import * as _ from 'styles/helpers'

const headerCells = [
	{ id: 'projectName', label: 'Project', sortable: true },
	{ id: 'projectDescription', label: 'Description', sortable: true },
	{ id: 'projectType', label: 'Project Type', sortable: true },
	{ id: 'tasksCount', label: 'Tasks', sortable: true, align: 'center' },
	{ id: 'imagesCount', label: 'Images', sortable: true, align: 'center' },
	{ id: 'documentsCount', label: 'Documents', sortable: true, align: 'center' },
	{ id: 'partners', label: 'Partners', sortable: false, align: 'left' },
	{ id: 'more', label: 'Owner/Actions', sortable: false, align: 'center' },
]

const getNestedValue = (obj, path) => {
	return path.split('.').reduce((acc, key) => acc?.[key], obj)
}

const getInitials = (name = '') => {
	const parts = name.trim().split(' ').filter(Boolean)
	if (parts.length === 0) return 'NA'
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
	return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase()
}

const PartnerPopover = ({ partners = [] }) => {
	const partnerUsers = partners
		.filter(partner => partner && partner.partnerUserId)
		.map(partner => partner.partnerUserId)

	if (!partnerUsers.length) {
		return <span className="text-sm text-gray-400">No partners</span>
	}

	return (
		<div className="flex items-center -space-x-2">
			{partnerUsers.map(user => (
				<Avatar
					key={user._id}
					className="h-8 w-8 border-2 border-white shadow-sm"
				>
					<AvatarImage src={user.image} />
					<AvatarFallback className="bg-slate-900 text-white text-xs">
						{getInitials(user.name)}
					</AvatarFallback>
				</Avatar>
			))}
		</div>
	)
}

const PartnerDetailsPopover = ({ partners = [] }) => {
	if (!partners.length) {
		return <span className="text-sm text-gray-400">No partners</span>
	}

	return (
		<div className="flex flex-col gap-2">
			{partners.map(user => (
				<div
					key={user._id}
					className="flex items-center gap-3 min-w-0"
				>
					<Avatar className="h-8 w-8 bg-slate-900 text-white text-xs flex-shrink-0">
						<AvatarImage src={user.image} />
						<AvatarFallback className="bg-slate-900 text-white text-xs">
							{getInitials(user.name)}
						</AvatarFallback>
					</Avatar>
					<div className="flex flex-col min-w-0">
						<span className="font-semibold whitespace-nowrap">
							{user.name || 'Unnamed Partner'}
						</span>
						{user.company && (
							<span className="text-xs opacity-85 whitespace-nowrap">
								{user.company}
							</span>
						)}
					</div>
				</div>
			))}
		</div>
	)
}

const TaskListPopover = ({ tasks = [] }) => {
	if (!tasks.length) {
		return <span className="text-sm text-gray-400">No tasks</span>
	}

	return (
		<div className="flex flex-col gap-2">
			{tasks.map(task => (
				<div
					key={task._id}
					className="flex items-center gap-3 min-w-0 max-w-[320px]"
				>
					<Avatar className="h-8 w-8 bg-slate-900 text-white text-xs flex-shrink-0">
						<AvatarImage src={task.assignedTo?.image} />
						<AvatarFallback className="bg-slate-900 text-white text-xs">
							{getInitials(task.assignedTo?.name || '')}
						</AvatarFallback>
					</Avatar>
					<div className="flex flex-col min-w-0">
						<span className="font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
							{task.taskName || 'Untitled Task'}
						</span>
						<span className="text-xs opacity-85">
							Due{' '}
							{task.completedBy
								? new Date(task.completedBy).toLocaleDateString(undefined, {
										month: '2-digit',
										day: '2-digit',
										year: '2-digit',
								  })
								: '--'}
						</span>
					</div>
				</div>
			))}
		</div>
	)
}

const InfoPopover = ({ items = [], label, renderItem }) => {
	const [isOpen, setIsOpen] = useState(false)

	const handleAnchorEnter = () => {
		if (items.length > 0) {
			setIsOpen(true)
		}
	}

	const handleAnchorLeave = () => {
		setIsOpen(false)
	}

	return (
		<div className="relative inline-flex items-center justify-center">
			<span
				onMouseEnter={handleAnchorEnter}
				onMouseLeave={handleAnchorLeave}
				className={`inline-flex items-center justify-center ${items.length > 0 ? 'cursor-pointer' : 'cursor-default'}`}
			>
				{label}
			</span>
			{isOpen && (
				<div
					className="absolute z-50 top-full mt-1 left-1/2 -translate-x-1/2 bg-gray-800 text-white shadow-lg rounded-lg px-4 py-3 text-sm max-w-[320px]"
					onMouseEnter={() => setIsOpen(true)}
					onMouseLeave={() => setIsOpen(false)}
				>
					{renderItem ? renderItem(items) : <TaskListPopover tasks={items} />}
				</div>
			)}
		</div>
	)
}

const ImageListPopover = ({ images = [] }) => {
	if (!images.length) {
		return <span className="text-sm text-gray-400">No images</span>
	}

	return (
		<div className="flex flex-col gap-2">
			{images.map(image => (
				<div
					key={image._id}
					className="flex items-center gap-3 min-w-0 max-w-[320px]"
				>
					<div className="h-10 w-10 rounded bg-slate-900 flex-shrink-0 overflow-hidden">
						<img src={image.url} alt="" className="h-full w-full object-cover" />
					</div>
					<div className="flex flex-col min-w-0">
						<span className="font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
							{image.name || 'Untitled Image'}
						</span>
						{image.size && (
							<span className="text-xs opacity-75">
								{Math.round(image.size / 1024)} KB
							</span>
						)}
					</div>
				</div>
			))}
		</div>
	)
}

const DOCUMENT_ICONS = {
	pdf: { color: '#ef4444', label: 'PDF' },
	doc: { color: '#2563eb', label: 'DOC' },
	docx: { color: '#2563eb', label: 'DOCX' },
	xls: { color: '#059669', label: 'XLS' },
	xlsx: { color: '#059669', label: 'XLSX' },
	ppt: { color: '#d97706', label: 'PPT' },
	pptx: { color: '#d97706', label: 'PPTX' },
	csv: { color: '#10b981', label: 'CSV' },
	txt: { color: '#6b7280', label: 'TXT' },
	default: { color: '#4b5563', label: 'DOC' },
}

const getDocumentIcon = name => {
	if (!name) return DOCUMENT_ICONS.default
	const extension = name.split('.').pop()?.toLowerCase()
	return DOCUMENT_ICONS[extension] || DOCUMENT_ICONS.default
}

const DocumentListPopover = ({ documents = [] }) => {
	if (!documents.length) {
		return <span className="text-sm text-gray-400">No documents</span>
	}

	return (
		<div className="flex flex-col gap-2">
			{documents.map(doc => (
				<div
					key={doc._id}
					className="flex items-center gap-3 min-w-0 max-w-[320px]"
				>
					{(() => {
						const icon = getDocumentIcon(doc.name)
						return (
							<div
								className="h-10 w-10 rounded flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
								style={{ backgroundColor: icon.color }}
							>
								{icon.label}
							</div>
						)
					})()}
					<div className="flex flex-col min-w-0">
						<span className="font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
							{doc.name || 'Untitled Document'}
						</span>
						{doc.docDate && (
							<span className="text-xs opacity-75">{doc.docDate}</span>
						)}
					</div>
				</div>
			))}
		</div>
	)
}

const LoctionProjects = ({ userId, location }) => {
	const dispatch = useDispatch()
	const isMobile = useMediaQuery(_.MQ.before_nav)
	const { canCreateAssets } = useCapabilities()

	const [orderBy, setOrderBy] = useState(null)
	const [order, setOrder] = useState('asc')

	const { id: locationId } = useParams()
	const ownerId = location.owner._id

	// Add New Project popup
	const [openPopup, setOpenPopup] = useState(false)
	const [popupTitle, setPopupTitle] = useState('')

	// Sheet state
	const [sheetOpen, setSheetOpen] = useState(false)
	const [selectedProject, setSelectedProject] = useState(null)

	const {
		entities: projectEntities,
		ids: projectIds,
		isLoading: projectsLoading,
		isInitialized,
		error: projectsError,
	} = useSelector(state => state.projects)
	const projects = projectIds
		.map(id => projectEntities[id])
		.filter(Boolean)
		.map(project => {
			const sortedTasks = Array.isArray(project.tasks)
				? project.tasks
						.filter(Boolean)
						.map(task => ({
							...task,
							assignedTo: task?.assignedTo || null,
						}))
						.sort((a, b) => {
							const dateA = a?.completedBy ? new Date(a.completedBy).getTime() : 0
							const dateB = b?.completedBy ? new Date(b.completedBy).getTime() : 0
							return dateA - dateB
						})
				: []

			const projectImages = Array.isArray(project.images)
				? project.images.filter(Boolean).map(image => ({
						...image,
						url: image?.url || image?.thumbnail || '',
						name: image?.name || image?.originalname || 'Untitled Image',
				  }))
				: []

			const projectDocuments = Array.isArray(project.documents)
				? project.documents.filter(Boolean).map(doc => ({
						...doc,
						name: doc?.name || doc?.originalname || 'Untitled Document',
				  }))
				: []

			const partnerUsers = Array.isArray(project.partners)
				? project.partners
						.filter(partner => partner && partner.partnerUserId)
						.map(partner => partner.partnerUserId)
				: []

			return {
				...project,
				tasks: sortedTasks,
				images: projectImages,
				partnerUsers,
				documents: projectDocuments,
			}
		})

	useEffect(() => {
		dispatch(initializeProjects({ locationId }))
	}, [dispatch, locationId])

	const handleProjectsAddNew = () => {
		setPopupTitle('New Project')
		setOpenPopup(true)
	}

	const handleSort = columnId => {
		if (orderBy === columnId) {
			setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setOrderBy(columnId)
			setOrder('asc')
		}
	}

	const sortedProjects = useMemo(() => {
		if (!orderBy) return projects

		return [...projects].sort((a, b) => {
			const aVal = (getNestedValue(a, orderBy) || '').toString().toLowerCase()
			const bVal = (getNestedValue(b, orderBy) || '').toString().toLowerCase()

			if (aVal < bVal) return order === 'asc' ? -1 : 1
			if (aVal > bVal) return order === 'asc' ? 1 : -1
			return 0
		})
	}, [projects, orderBy, order])

	const SortIcon = ({ columnId }) => {
		if (orderBy !== columnId) return <ArrowUpDown className="ml-1 h-3 w-3 text-gray-300" />
		return order === 'asc' ? (
			<ArrowUp className="ml-1 h-3 w-3 text-primary" />
		) : (
			<ArrowDown className="ml-1 h-3 w-3 text-primary" />
		)
	}

	const handleThreeDotClick = (event, project) => {
		event.stopPropagation()
		setSelectedProject(project)
		setSheetOpen(true)
	}

	const handleCreateProject = async newProject => {
		await dispatch(
			createProject({
				owner: userId,
				projectName: newProject.projectName,
				projectDescription: newProject.projectDescription,
				projectType: newProject.projectType,
				projectAsset: locationId,
				updateNote: 'New Project',
			}),
		)
		dispatch(initializeProjects({ locationId }))
		setOpenPopup(false)
		dispatch(patchLocation({ locationId, updates: { updateNote: 'New Project(s) Added' } }))
	}

	const handleEditProject = async values => {
		await dispatch(
			patchProject({
				projectId: values._id,
				updates: {
					projectName: values.projectName,
					projectDescription: values.projectDescription,
					projectType: values.projectType,
					isActive: values.isActive,
					updateNote: 'Project edited.',
				},
			}),
		)
		dispatch(initializeProjects({ locationId }))
		dispatch(patchLocation({ locationId, updates: { updateNote: 'Project Edited' } }))
	}

	const handleDeleteProject = async () => {
		if (!selectedProject) return
		await dispatch(deleteProject({ projectId: selectedProject._id }))
		dispatch(initializeProjects({ locationId }))
		dispatch(patchLocation({ locationId, updates: { updateNote: 'Project Deleted' } }))
	}

	if (projectsLoading) return <Spinner containerHeight="50vh" />
	if (projectsError) return <Message variant="danger">{projectsError}</Message>

	const hasProjects = projects.length > 0

	return (
		<div className="flex flex-col gap-6">
			{hasProjects && (
				<div className="flex flex-wrap justify-between items-center gap-4">
					<div className="flex flex-col gap-1">
						<h2 className="text-2xl font-bold m-0">Projects</h2>
						<span className="text-base text-gray-600">Plan and organize initiatives for this location.</span>
					</div>
					{canCreateAssets && (
						<Button variant="primary" isInput onClick={handleProjectsAddNew}>
							<span className="flex items-center gap-2">
								<Plus className="w-3.5 h-3.5" />
								Create New Project
							</span>
						</Button>
					)}
				</div>
			)}

			<div>
				<div>
					{hasProjects ? (
						isMobile ? (
							<CardGrid>
								{sortedProjects.map(project => (
									<ProjectCard
										key={project._id}
										name={project.projectName}
										description={project.projectDescription}
										projectType={project.projectType}
										locationName={location.assetName}
										locationCityProv={`${location.city}, ${location.stateProvince}`}
										image={location.image}
										linkUrl={`/locations/${locationId}/projects/${project._id}`}
										isOwner={project.owner._id === userId}
										ownerImage={project.owner.image}
										onThreeDotClick={() => {
											setSelectedProject(project)
											setSheetOpen(true)
										}}
									/>
								))}
							</CardGrid>
						) : (
							<Table>
								<TableHeader>
									<TableRow className="border-b-2 border-[#027fff] hover:bg-transparent">
										{headerCells.map(cell => (
											<TableHead
												key={cell.id}
												className={
													cell.sortable
														? `cursor-pointer select-none text-gray-700 text-xs font-semibold whitespace-nowrap${cell.align === 'center' ? ' text-center' : ''}`
														: `text-gray-700 text-xs font-semibold whitespace-nowrap${cell.align === 'center' ? ' text-center' : ''}`
												}
												onClick={cell.sortable ? () => handleSort(cell.id) : undefined}
											>
												<span className={`inline-flex items-center${cell.align === 'center' ? ' justify-center w-full' : ''}`}>
													{cell.label}
													{cell.sortable && <SortIcon columnId={cell.id} />}
												</span>
											</TableHead>
										))}
									</TableRow>
								</TableHeader>
								<TableBody>
									{sortedProjects.map(project => (
										<TableRow
											key={project._id}
											className="h-7 hover:bg-[#fffbf2] cursor-pointer"
										>
											<TableCell className="py-1">
												<Link
													to={`/locations/${locationId}/projects/${project._id}`}
													className="text-primary hover:underline font-medium text-sm"
												>
													{project.projectName}
												</Link>
											</TableCell>
											<TableCell className="py-1 text-sm">{project.projectDescription}</TableCell>
											<TableCell className="py-1 text-sm">{project.projectType}</TableCell>
											<TableCell className="py-1 text-sm text-center">
												<InfoPopover items={project.tasks || []} label={project.tasksCount ?? 0} />
											</TableCell>
											<TableCell className="py-1 text-sm text-center">
												<InfoPopover
													items={project.images || []}
													label={project.imagesCount ?? 0}
													renderItem={items => <ImageListPopover images={items} />}
												/>
											</TableCell>
											<TableCell className="py-1 text-sm text-center">
												<InfoPopover
													items={project.documents || []}
													label={project.documentsCount ?? 0}
													renderItem={items => <DocumentListPopover documents={items} />}
												/>
											</TableCell>
											<TableCell className="py-1 text-sm">
												<InfoPopover
													items={project.partnerUsers || []}
													label={<PartnerPopover partners={project.partners} />}
													renderItem={() => (
														<PartnerDetailsPopover partners={project.partnerUsers || []} />
													)}
												/>
											</TableCell>
											<TableCell className="py-1 text-sm text-center">
												{project.owner._id === userId ? (
													<button
														type="button"
														className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors"
														onClick={e => handleThreeDotClick(e, project)}
													>
														<MoreHorizontal className="h-4 w-4" />
													</button>
												) : (
													<div className="flex justify-center w-full" title={project.owner.name || 'Owner'}>
														<Avatar className="h-8 w-8 bg-slate-900 text-white text-xs">
															<AvatarImage src={project.owner.image} />
															<AvatarFallback className="bg-slate-900 text-white text-xs">
																{getInitials(project.owner.name)}
															</AvatarFallback>
														</Avatar>
													</div>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						)
					) : (
						<LocationProjectsEmptyState onCreateProject={canCreateAssets ? handleProjectsAddNew : undefined} />
					)}
				</div>
			</div>

			{openPopup ? (
				<Popup title={popupTitle} openPopup={openPopup} setOpenPopup={setOpenPopup} icon={<FolderKanban className="w-5 h-5" />} description="Add a project to this location">
					<ProjectAddPopup addProject={handleCreateProject} />
				</Popup>
			) : null}

			<ProjectActionSheet
				project={selectedProject}
				open={sheetOpen}
				onClose={() => setSheetOpen(false)}
				onEdit={handleEditProject}
				onDelete={handleDeleteProject}
			/>
		</div>
	)
}

export default LoctionProjects
